function ReferenceEditor({
  title,
  copy,
  listTitle,
  records,
  selectedId,
  onSelect,
  onEdit,
  onAddNew,
  onDelete,
  isLoadingList,
  value,
  onChange,
  onSave,
  isSaving,
  saveMessage,
  fields,
}) {
  const columnTemplate = `${fields
    .map((field) => field.columnWidth ?? 'minmax(160px, 1fr)')
    .join(' ')} 120px`;
  const getOptionLabel = (field, fieldValue) => {
    const match = field.options?.find((option) => String(option.value) === String(fieldValue));
    return match ? match.label : String(fieldValue);
  };

  const renderValue = (field, record) => {
    const fieldValue = record?.[field.key];
    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
      return '';
    }
    if (field.options?.length) {
      return getOptionLabel(field, fieldValue);
    }
    return String(fieldValue);
  };

  const renderEditableCell = (field) => {
    const fieldValue = value?.[field.key] ?? '';
    const inputClassName = `editor-input reference-grid-input${
      field.align === 'center' ? ' reference-grid-input-center' : ''
    }`;

    if (field.options?.length) {
      return (
        <select
          key={field.key}
          className={`select-input ${inputClassName}`}
          value={fieldValue}
          onChange={(event) => onChange(field.key, event.target.value)}
        >
          <option value="">Select...</option>
          {field.options.map((option) => (
            <option key={`${field.key}-${option.value}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          key={field.key}
          className={`text-input ${inputClassName}`}
          value={fieldValue}
          onChange={(event) => onChange(field.key, event.target.value)}
          rows={4}
        />
      );
    }

    return (
      <input
        key={field.key}
        className={`text-input ${inputClassName}`}
        type={field.type === 'number' ? 'number' : 'text'}
        value={fieldValue}
        onChange={(event) => onChange(field.key, event.target.value)}
      />
    );
  };

  const renderReadOnlyRow = (record) => {
    const recordId = String(record.id);
    const isSelected = selectedId === recordId;

    return (
      <div
        key={recordId}
        className={`reference-grid-row reference-grid-button-row ${isSelected ? 'active' : ''}`}
        style={{ gridTemplateColumns: columnTemplate }}
        role="button"
        tabIndex={0}
        onClick={() => { if (!isSaving) onSelect(recordId); }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!isSaving) onSelect(recordId);
          }
        }}
      >
        {fields.map((field) => (
          <span
            key={`${recordId}-${field.key}`}
            className={`reference-grid-cell${field.type === 'textarea' ? ' reference-grid-cell-multiline' : ''}${
              field.align === 'center' ? ' reference-grid-cell-center' : ''
            }`}
          >
            {renderValue(field, record.raw)}
          </span>
        ))}
        <div className="reference-grid-cell reference-grid-action-cell">
          <button
            type="button"
            className="text-button"
            disabled={isSaving}
            onClick={(event) => {
              event.stopPropagation();
              onEdit(recordId);
            }}
          >
            Edit
          </button>
          {onDelete && <button type="button" className="text-button" disabled={isSaving} onClick={(event) => { event.stopPropagation(); onDelete(recordId); }}>Delete</button>}
        </div>
      </div>
    );
  };

  const renderEditableRow = () => (
    <div
      key={selectedId || '__new__'}
      className="reference-grid-row reference-grid-edit-row"
      style={{ gridTemplateColumns: columnTemplate }}
    >
      {fields.map((field) => renderEditableCell(field))}
      <div className="reference-grid-cell reference-grid-action-cell">
        <button type="button" className="button-with-icon" onClick={onSave} disabled={isSaving}>
          <span>{isSaving ? 'Saving...' : 'Save'}</span>
        </button>
        {onDelete && selectedId !== '__new__' && <button type="button" className="text-button" disabled={isSaving} onClick={() => onDelete(selectedId)}>Delete</button>}
      </div>
    </div>
  );

  return (
    <section className="panel">
      <div className="panel-header-row">
        <div>
          <h2>{title}</h2>
          <p className="panel-copy">{copy}</p>
        </div>
        <div className="button-row compact">
          <button type="button" className="secondary-button" onClick={onAddNew} disabled={isSaving}>
            Add New
          </button>
        </div>
      </div>

      {saveMessage && <div className="panel-status">{saveMessage}</div>}

      <section className="editor-section">
        <h3>{listTitle}</h3>

        <div className="reference-grid">
          <div className="reference-grid-row editor-table-head" style={{ gridTemplateColumns: columnTemplate }}>
            {fields.map((field) => (
              <span key={`head-${field.key}`}>{field.label}</span>
            ))}
            <span>Action</span>
          </div>

          {selectedId === '__new__' && value && renderEditableRow()}

          {isLoadingList && <div className="empty-grid">Loading records...</div>}

          {!isLoadingList &&
            records.map((record) =>
              selectedId === String(record.id) && value ? renderEditableRow() : renderReadOnlyRow(record)
            )}

          {!isLoadingList && records.length === 0 && selectedId !== '__new__' && (
            <div className="empty-grid">No records found.</div>
          )}
        </div>
      </section>
    </section>
  );
}

export default ReferenceEditor;
